/**
 * AddRoadmapPage
 *
 * Web port of Flutter's `AddRoadmapItemScreen`
 * (`lib/presentation/screens/roadmap/add_roadmap_item_screen.dart:137-322`):
 * an app bar ("Add idea" + back arrow), an intro disclaimer, a title field
 * (max 100 chars) and a description field (max 300 chars), and a "Done" submit
 * button. The submit flow (confirm → POST → success/error sheet → pop) matches
 * Flutter exactly via `useAddRoadmapItem`; the page only supplies the
 * presentation surfaces (sheets + navigate-back) and the current user.
 *
 * Flutter swaps the whole body for a centered loading indicator while
 * submitting; the web overlays it (`position: fixed`) per the established
 * `ContactPage` pattern.
 *
 * App-bar provenance: the bar is the shared `ExpauseAppBar`
 * (`presentation/components/common/appBar/ExpauseAppBar.tsx`). Flutter builds
 * this screen's bar as a **raw inline `AppBar`** in its own `appBar()` helper
 * (`add_roadmap_item_screen.dart:155-171`, mounted at `:142`) rather than via the
 * shared `BaseAppBar` widget, but its shape is the same one `ExpauseAppBar` ports
 * (`lib/presentation/custom_widgets/base_app_bar.dart:5-42`: back leading +
 * `centerTitle: true` + `kToolbarHeight`). The leading handler keeps this page's
 * own deep-link fallback — **Roadmap**, not Feed (see `handleBack`).
 *
 * Moving from the hand-rolled bar to `ExpauseAppBar` makes three intentional
 * visual changes: the title steps from `responsive.font.lg`/`semibold` to
 * `responsive.font.xl`/`bold`, the bar becomes sticky with a blurred
 * 50%-opacity backdrop, and the top safe-area inset is now honoured. The
 * submitting overlay (`position: fixed`, `zIndex: 9999`) still paints above the
 * bar's `zIndex: 50`.
 *
 * The two-field body lives in `./components/AddRoadmapForm` to keep this file
 * under the ~200-line Should-Fix threshold from `presentation.md`.
 *
 * UI conventions (theme tokens, responsive sizing, sheets-not-dialogs) deviate
 * from Flutter per `presentation.md`.
 */
import { useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import type { RootState } from '@/presentation/store';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { Navigator } from '@/presentation/routing/Navigator';
import { ExpauseAppBar } from '@/presentation/components/common/appBar/ExpauseAppBar';
import { ExpauseLoadingIndicator } from '@/presentation/components/common/loadingIndicators/ExpauseLoadingIndicator';
import { useBasePage } from '@/presentation/hooks/useBasePage';
import { useKeepAlive } from '@/presentation/routing/RouteStack';
import { generateScreenKey } from '@/presentation/routing/screenKeyUtils';
import { qaAttr } from '@/presentation/utils/qaAttr';
import { useAddRoadmapItem } from './hooks/useAddRoadmapItem';
import { useAddRoadmapSheetSurface } from './hooks/useAddRoadmapSheetSurface';
import { AddRoadmapForm } from './components/AddRoadmapForm';

export const AddRoadmapPage = () => {
    const { theme } = useTheme();
    const responsive = useResponsiveDesign();
    const { l10n } = useLocale();
    const location = useLocation();

    // Canonical screen-key wiring (mirrors `ContactPage`).
    const { currentScreenKeyRef } = useKeepAlive();
    const screenKeyRef = useRef<string>(
        currentScreenKeyRef.current || generateScreenKey(location.pathname)
    );
    const screenKey = screenKeyRef.current;

    useBasePage({ screenKey, onInit: () => { } });

    // Author stamped on the new item — Flutter reads
    // `currentUser.firebaseId` / `currentUser.name`
    // (add_roadmap_item_screen.dart:101-102). Submit is gated on a non-null
    // user via the `canSubmit` check below.
    const currentUser = useSelector((state: RootState) => state.auth.user);

    // Surface wiring for `useAddRoadmapItem` — the hook owns state +
    // orchestration, the page picks the sheet surface.
    const surface = useAddRoadmapSheetSurface(screenKey);
    const { title, description, isSubmitting, canSubmit, setTitle, setDescription, submit } =
        useAddRoadmapItem({
            currentUser: {
                firebaseId: currentUser?.firebaseId ?? '',
                name: currentUser?.name ?? '',
            },
            ...surface,
        });

    // Gate submit on an authenticated user in addition to the hook's
    // field/submitting predicate.
    const canSubmitGated = canSubmit && currentUser !== null;

    // Deep-link guard (same pattern as ContactPage): with no in-app
    // history, fall back to the roadmap list.
    const handleBack = () => Navigator.goBackOr(() => Navigator.navigateToRoadmap());

    return (
        <div
            {...qaAttr({ id: 'add-roadmap', status: isSubmitting ? 'submitting' : 'idle' })}
            style={{
                position: 'relative',
                minHeight: '100vh',
                backgroundColor: theme.background,
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            {/* App bar — back button + centered title (mirrors ContactPage). */}
            <ExpauseAppBar
                rootQa={{ id: 'add-roadmap-app-bar' }}
                title={l10n.roadmap.addIdeaTitle}
                leadingQa={{ id: 'add-roadmap-back' }}
                onLeadingTap={handleBack}
            />

            {/* Body — centered column with intro + two fields + submit. */}
            <div
                style={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    padding: `${responsive.spacing.lg}px`,
                }}
            >
                <div
                    style={{
                        width: '100%',
                        maxWidth: responsive.dialog.maxWidth,
                    }}
                >
                    <AddRoadmapForm
                        title={title}
                        description={description}
                        canSubmit={canSubmitGated}
                        setTitle={setTitle}
                        setDescription={setDescription}
                        onSubmit={submit}
                    />
                </div>
            </div>

            {/* Loading overlay while submitting. Flutter swaps the whole body
                for a centered indicator (add_roadmap_item_screen.dart:146-150);
                web overlays it with the established `ContactPage` block
                (`position: fixed` keeps the spinner viewport-centered even when
                the page scrolls; `zIndex: 9999` sits above sheet z-indices). */}
            {isSubmitting && (
                <div
                    style={{
                        position: 'fixed',
                        inset: 0,
                        backgroundColor: theme.modalOverlay,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        zIndex: 9999,
                    }}
                >
                    <ExpauseLoadingIndicator />
                </div>
            )}
        </div>
    );
};
